import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "../../UIElements/Icon";
import {
  getUserName,
  getUserPhone,
  getUserInitials,
  getUserOrdersCount,
  getUserCardTheme,
} from "../../../../constants/userConstants";

const UserCard = ({ user, onViewDetails }) => {
  const { t } = useTranslation("customers");
  const rawName = getUserName(user);
  const name = rawName === "User" ? t("card.userFallback") : rawName;
  const phone = getUserPhone(user, t("card.noData"));
  const email = user?.email || t("card.noEmail");
  const initials = getUserInitials(user);
  const ordersCount = getUserOrdersCount(user);
  const levelLabelByUserLevel = {
    0: t("levels.admin"),
    2: t("levels.businessOwner"),
    3: t("levels.customer"),
  };
  const levelLabel = levelLabelByUserLevel[user?.level] || t("levels.user");
  const theme = getUserCardTheme(user.enabled);

  const handleViewDetails = () => {
    if (onViewDetails) {
      onViewDetails(user);
    }
  };

  return (
    <div
      className={`cursor-pointer rounded-lg border p-3 transition-colors sm:p-4 ${theme.card}`}
      onClick={handleViewDetails}
    >
      {/* Header with Avatar */}
      <div className="mb-3 flex items-center space-x-2 sm:space-x-3">
        <div className="flex h-8 w-8 flex-shrink-0 items-center justify-center overflow-hidden rounded-full sm:h-10 sm:w-10">
          {user.photo ? (
            <img
              src={user.photo}
              alt={name}
              className="h-full w-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails to load
                e.target.style.display = "none";
                e.target.nextSibling.style.display = "flex";
              }}
            />
          ) : null}
          <div
            className={`h-full w-full bg-gradient-to-br ${theme.avatar} flex items-center justify-center text-xs font-semibold text-white sm:text-sm ${user.photo ? "hidden" : "flex"}`}
          >
            {initials}
          </div>
        </div>
        <div className="min-w-0 flex-1">
          <h3
            className={`truncate text-sm font-semibold sm:text-base ${theme.name}`}
          >
            {name}
          </h3>
          <p className={`text-xs ${theme.role}`}>{levelLabel}</p>
        </div>
      </div>

      {/* Contact Information */}
      <div className="mb-3 space-y-1">
        <div className={`flex items-center space-x-2 ${theme.contact}`}>
          <Icon name="Phone" className="h-3 w-3 flex-shrink-0" />
          <span className="truncate text-xs">{phone}</span>
        </div>
        <div className={`flex items-center space-x-2 ${theme.contact}`}>
          <Icon name="Mail" className="h-3 w-3 flex-shrink-0" />
          <span className="truncate text-xs">{email}</span>
        </div>
      </div>

      {/* Statistics */}
      {user?.level !== 0 && (
        <div className="grid grid-cols-1 gap-2 sm:gap-3">
          <div className="text-center">
            <p className={`text-xs ${theme.stats.label}`}>{t("card.orders")}</p>
            <p
              className={`text-xs font-semibold sm:text-sm ${theme.stats.value}`}
            >
              {ordersCount || "0"}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserCard;
